/*
 * Intelligent Fabric — Admin Console sidebar.
 *
 * Standard Backstage Root.tsx shape (drops into a create-app scaffold) with the
 * IF logo + the 8 operator surfaces from the approved sketch.
 *
 * NOTE: /identity, /policy, /compliance, /observability, /approvals are custom
 * operator surfaces delivered as plugin pages in Phases 30/31 (Tech Insights
 * scorecards, Cedar policy explorer, NATS inspector, HITL approvals). Until
 * those land, register them in App.tsx as placeholder pages so the nav never
 * 404s (see MIGRATION.md §4).
 */
import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import PeopleIcon from '@material-ui/icons/People';
import SecurityIcon from '@material-ui/icons/Security';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import TimelineIcon from '@material-ui/icons/Timeline';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'flex-start',
    marginBottom: -14,
  },
  link: { width: sidebarConfig.drawerWidthClosed, marginLeft: 24 },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Operate */}
        <SidebarItem icon={HomeIcon} to="/" text="Overview & Fleet" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="MCP APIs" />
        <SidebarItem icon={TimelineIcon} to="observability" text="Observability" />
        <SidebarDivider />
        {/* Govern */}
        <SidebarItem icon={CreateComponentIcon} to="create" text="Agent Factory" />
        <SidebarItem icon={PeopleIcon} to="identity" text="Identity & Access" />
        <SidebarItem icon={SecurityIcon} to="policy" text="Policy Explorer" />
        <SidebarItem icon={VerifiedUserIcon} to="compliance" text="Compliance" />
        <SidebarItem icon={AssignmentTurnedInIcon} to="approvals" text="Approvals" />
        <SidebarDivider />
        <SidebarScrollWrapper>
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);

export default Root;
